/**
 * A sub's documents sent to the job's closeout library (Duncan 2026-10-25: the PM sends them -- "an easy way ... they
 * can select 1 or multiople files, folders etc..." -- and picks where they land). Both ways in from the mockup he
 * picked: the Closeout tab's Add from the subs (SubsDocumentsPicker: tick, then "Add N files here", into the folder open
 * there) and a Documents task's window (tick, then Send to Closeout…: CloseoutFolderPicker, + New folder). A ticked
 * folder comes with its subfolders; one of E3 Subs' five built-in folders puts its files straight in. Summit's server
 * copies them (server/subsToCloseout.js); a file in Closeout already asks first: another copy, or skip it (Duncan).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCloseout, createCloseoutFolder, getCloseoutFromSubs, sendToCloseout, getCloseoutSend, sendToCloseoutAgain } from '../../api';
import { ErrorBanner, PrimaryButton, SecondaryButton, fieldStyle } from './SubUi';

const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const box = { padding: '10px 12px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.45, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', color: 'var(--text-1)' };
const tone = (v) => ({ ...box, background: `var(--${v}-soft)`, border: `1px solid color-mix(in oklch, var(--${v}) 22%, transparent)` });
const check = { margin: 0, width: 15, height: 15, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'pointer' };

const Glyph = ({ children, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>{children}</svg>
);
const FOLDER = <path d="M3.5 6.5A1.5 1.5 0 0 1 5 5h3.8a1.5 1.5 0 0 1 1.1.5l1.2 1.3a1.5 1.5 0 0 0 1.1.5H19A1.5 1.5 0 0 1 20.5 9v9A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z" />;
const FILE = <><path d="M6.5 3.5h7l4 4v12a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1Z" /><path d="M13.5 3.5v4h4" /></>;
const CHEVRON = <path d="M9 6l6 6-6 6" />;
export const SEND = <><path d="M4 12 20 4l-4 16-4-6.5Z" /><path d="M12 13.5 20 4" /></>;

function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (v < 1024) return `${v} B`;
  const kb = v / 1024; if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024; if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}
const plural = (n, one) => `${n.toLocaleString('en-US')} ${one}${n === 1 ? '' : 's'}`;
const byName = (a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true, sensitivity: 'base' });

/** A task's folders and files, for ticking: its folders by parent, its files by folder, and what's ticked inside what. */
export function useTicks(folders, documents) {
  const [ticked, setTicked] = useState(() => new Map()); // `${kind}:${id}` -> { kind, id, taskId }
  const parentOf = useMemo(() => new Map((folders || []).map((f) => [f.id, f.parentId || null])), [folders]);
  /** Inside a ticked folder (so it's going already). */
  const covered = useCallback((folderId, taskId) => {
    for (let id = folderId, i = 0; id && i < 40; id = parentOf.get(id), i += 1) if (ticked.has(`folder:${taskId}:${id}`)) return true;
    return false;
  }, [parentOf, ticked]);
  const toggle = useCallback((kind, id, taskId) => setTicked((m) => {
    const next = new Map(m);
    const key = `${kind}:${taskId}:${id}`;
    if (next.has(key)) next.delete(key);
    else {
      next.set(key, { kind, id, taskId });
      // A folder ticked takes what's inside it: anything ticked in there goes with it.
      if (kind === 'folder') {
        const inside = (fid) => { for (let x = fid, i = 0; x && i < 40; x = parentOf.get(x), i += 1) if (x === id) return true; return false; };
        [...next.entries()].forEach(([k, v]) => {
          if (k === key || v.taskId !== taskId) return;
          const at = v.kind === 'folder' ? parentOf.get(v.id) : ((documents || []).find((d) => d.id === v.id) || {}).folderId;
          if (inside(at)) next.delete(k);
        });
      }
    }
    return next;
  }), [parentOf, documents]);
  const clear = useCallback(() => setTicked(new Map()), []);
  return { ticked, covered, toggle, clear, items: useMemo(() => [...ticked.values()], [ticked]) };
}

/** A send to Closeout: asks first when some are there already, then follows it until it's done. */
export function useCloseoutSend(projectId, onDone) {
  const [ask, setAsk] = useState(null); // the 409 ALREADY_SENT, with what was being sent: { items, folderId, error, count, total, names }
  const [send, setSend] = useState(null); // { id, state: 'going' | 'done' | 'gone', landing, total, done, bytesTotal, bytesDone, failed }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const doneRef = useRef(onDone);
  useEffect(() => { doneRef.current = onDone; }, [onDone]);

  const start = useCallback(async (items, folderId, again) => {
    setBusy(true);
    setError(null);
    try {
      const s = await sendToCloseout(projectId, { items, folderId: folderId || null, ...(again ? { again } : {}) });
      setAsk(null);
      setSend(s);
      return s;
    } catch (e) {
      if (e.code === 'ALREADY_SENT') setAsk({ ...(e.body || {}), items, folderId, error: e.message });
      else { setAsk(null); setError(e.message || 'They weren’t sent'); }
      return null;
    } finally {
      setBusy(false);
    }
  }, [projectId]);
  const answer = useCallback((choice) => {
    if (!ask) return Promise.resolve(null);
    if (choice === 'cancel') { setAsk(null); return Promise.resolve(null); }
    return start(ask.items, ask.folderId, choice);
  }, [ask, start]);

  // How it's going, asked every second and a half until it's done.
  const goingId = send && send.state === 'going' ? send.id : null;
  useEffect(() => {
    if (!goingId) return undefined;
    let stopped = false;
    let timer = null;
    const tick = async () => {
      try {
        const s = await getCloseoutSend(projectId, goingId);
        if (stopped) return;
        setSend(s);
        if (s.state !== 'going') { if (doneRef.current) doneRef.current(s); return; }
      } catch (e) {
        if (stopped) return;
        if (e.code === 'SEND_GONE') {
          setSend((was) => (was ? { ...was, state: 'gone' } : was));
          setError(e.message);
          if (doneRef.current) doneRef.current(null);
          return;
        }
      }
      timer = setTimeout(tick, 1500);
    };
    timer = setTimeout(tick, 1000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [projectId, goingId]);

  const again = useCallback(async () => {
    if (!send) return;
    setBusy(true);
    setError(null);
    try {
      setSend(await sendToCloseoutAgain(projectId, send.id));
    } catch (e) {
      setError(e.message || 'They weren’t sent');
    } finally {
      setBusy(false);
    }
  }, [projectId, send]);
  const dismiss = useCallback(() => { setSend(null); setError(null); }, []);
  return { ask, answer, send, start, again, dismiss, busy, error };
}

/** Some picked are in Closeout already: send another copy, or skip those (Duncan: "Ask first"). */
export function AlreadyThere({ ask, busy, onAnswer }) {
  if (!ask) return null;
  const names = ask.names || [];
  const more = (ask.count || 0) - names.length;
  return (
    <div role="alert" style={tone('warning')}>
      <div><strong style={{ fontWeight: 650 }}>{ask.error}</strong>{names.length > 0 && ` ${names.join(', ')}${more > 0 ? `, and ${more} more` : ''}.`}</div>
      <div style={{ color: 'var(--text-2)', marginTop: 2 }}>Send another copy?</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <SecondaryButton busy={busy} onClick={() => onAnswer('send')} style={{ height: 30, fontSize: 13 }}>Send again</SecondaryButton>
        {ask.count < ask.total && (
          <SecondaryButton busy={busy} onClick={() => onAnswer('skip')} style={{ height: 30, fontSize: 13 }}>Skip {ask.count === 1 ? 'that one' : 'those'}</SecondaryButton>
        )}
        <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => onAnswer('cancel')}>Cancel</button>
      </div>
    </div>
  );
}

/** How a send is going, and how it went. */
export function SendProgress({ send, error, busy, onAgain, onDismiss, note = 'It carries on if you close this.' }) {
  if (!send) return error ? <ErrorBanner>{error}</ErrorBanner> : null;
  const into = <strong style={{ fontWeight: 650 }}>Closeout{send.landing ? ` › ${send.landing}` : ''}</strong>;
  if (send.state === 'gone') return <ErrorBanner>{error || 'That send stopped: Summit restarted. What went is in Closeout; send the rest again.'}</ErrorBanner>;
  if (send.state === 'going') {
    const pct = send.bytesTotal ? Math.min(100, Math.round((send.bytesDone / send.bytesTotal) * 100)) : Math.round((send.done / Math.max(1, send.total)) * 100);
    return (
      <div style={box} role="status">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Glyph>{SEND}</Glyph>
          <span>Sending {Math.min(send.total, send.done + send.failed.length + 1)} of {plural(send.total, 'file')} to {into}</span>
        </div>
        <div style={{ ...muted, margin: '2px 0 6px 23px' }}>{formatBytes(send.bytesDone)} of {formatBytes(send.bytesTotal)} · {note}</div>
        <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', marginLeft: 23 }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: 'var(--accent)', transition: 'width 300ms' }} />
        </div>
      </div>
    );
  }
  const failed = send.failed || [];
  return (
    <div style={tone(failed.length ? 'warning' : 'success')} role="status">
      {failed.length === 0
        ? <div>Sent {plural(send.done, 'file')} to {into}.</div>
        : <div>{send.done} of {plural(send.total, 'file')} went to {into}. {failed.length === 1 ? 'One didn’t:' : `${failed.length} didn’t:`}</div>}
      {failed.length > 0 && (
        <ul style={{ margin: '4px 0 0', paddingLeft: 20, color: 'var(--text-2)' }}>
          {failed.slice(0, 5).map((f, i) => <li key={`${f.name}-${i}`}>{f.name}: {f.error}</li>)}
          {failed.length > 5 && <li>and {failed.length - 5} more</li>}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
        {failed.length > 0 && <SecondaryButton busy={busy} onClick={onAgain} style={{ height: 30, fontSize: 13 }}>Try again</SecondaryButton>}
        <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={onDismiss}>Done</button>
      </div>
    </div>
  );
}

/** Which Closeout folder they go to (the Documents window's Send to Closeout…), and + New folder in the one picked. */
export function CloseoutFolderPicker({ projectId, count, busy, onPick, onCancel }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [chosen, setChosen] = useState(undefined); // a folder's id, null = the top, undefined = none yet
  const [naming, setNaming] = useState(null);
  const [making, setMaking] = useState(false);
  const load = useCallback(() => getCloseout(projectId)
    .then((d) => { setInfo(d); if (d && d.error) setError(d.error.error); return d; })
    .catch((e) => { setInfo((was) => was || {}); setError(e.message || 'Closeout couldn’t be read'); return null; }), [projectId]);
  useEffect(() => { load(); }, [load]);
  const folders = useMemo(() => (info && info.tree && info.tree.folders) || [], [info]);
  const rows = useMemo(() => {
    const out = [];
    const walk = (parentId, deep) => folders.filter((f) => (f.parentId || null) === parentId).sort(byName)
      .forEach((f) => { out.push({ f, deep }); walk(f.id, deep + 1); });
    walk(null, 1);
    return out;
  }, [folders]);
  const nameOf = (id) => (id ? (folders.find((f) => f.id === id) || {}).name : (info && info.link && info.link.name) || 'Top level');
  const makeFolder = async () => {
    const name = String(naming || '').replace(/\s+/g, ' ').trim();
    if (!name) return;
    setMaking(true);
    setError(null);
    try {
      const f = await createCloseoutFolder(projectId, chosen || null, name);
      await load();
      setNaming(null);
      if (f && f.id) setChosen(f.id);
    } catch (e) {
      setError(e.message || 'The folder wasn’t made');
    } finally {
      setMaking(false);
    }
  };

  const shell = (children, footer) => (
    <div style={{ flex: 1, minHeight: 200, borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ ...muted, padding: '9px 12px 6px' }}>Send {count ? <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{count}</strong> : 'them'} to which Closeout folder?</div>
      {children}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
        {footer}
        <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
  if (!info) return shell(<div style={{ ...muted, padding: '6px 12px', flex: 1 }}>Reading Closeout…</div>, null);
  if (!info.link) return shell(<div style={{ ...muted, padding: '6px 12px', flex: 1 }}>This job isn’t linked to its Facility Navigator library yet. Link it on the Closeout tab first.</div>, null);
  if (!info.canManage) return shell(<div style={{ ...muted, padding: '6px 12px', flex: 1 }}>You don’t have permission to change closeout documents.</div>, null);
  const row = (id, name, deep) => (
    <button key={id || 'top'} type="button" role="option" aria-selected={chosen === id} className={`tdoc-row${chosen === id ? ' on' : ''}`}
      onClick={() => setChosen(id)} style={{ paddingLeft: 12 + deep * 18 }}>
      <Glyph>{FOLDER}</Glyph>
      <span className="tdoc-name">{name}</span>
    </button>
  );
  return shell(
    <>
      {error && <div style={{ padding: '0 12px 6px' }}><ErrorBanner>{error}</ErrorBanner></div>}
      <div role="listbox" aria-label="The Closeout folder they go to" style={{ flex: 1, overflowY: 'auto' }}>
        {row(null, `${(info.link && info.link.name) || 'Closeout'} (the top)`, 0)}
        {rows.map(({ f, deep }) => row(f.id, f.name, deep))}
      </div>
    </>,
    naming !== null ? (
      <form onSubmit={(e) => { e.preventDefault(); makeFolder(); }} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: '1 1 auto' }}>
        <input autoFocus value={naming} maxLength={80} aria-label="The new folder’s name" placeholder={`A folder in ${nameOf(chosen || null)}`}
          onChange={(e) => setNaming(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setNaming(null); }}
          style={{ ...fieldStyle, height: 30, width: 220 }} />
        <SecondaryButton type="submit" busy={making} style={{ height: 30, fontSize: 13 }}>Add folder</SecondaryButton>
        <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setNaming(null)}>Never mind</button>
      </form>
    ) : (
      <>
        <PrimaryButton busy={busy} disabled={chosen === undefined} onClick={() => onPick(chosen || null, nameOf(chosen || null))} style={{ height: 30, fontSize: 13 }}>
          Send here
        </PrimaryButton>
        <button type="button" style={linkBtn} onClick={() => setNaming('')}>+ New folder</button>
      </>
    ),
  );
}

/**
 * The Closeout tab's Add from the subs: every Documents task's files and folders on this job, to tick, then "Add N files
 * here" into the folder open on the tab. A file already sent (and still in Closeout) says so.
 */
export function SubsDocumentsPicker({ projectId, folderName, sending, onAdd, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => {
    getCloseoutFromSubs(projectId).then(setData).catch((e) => { setError(e.message || 'The subs’ documents couldn’t be read'); setData({ on: true, tasks: [] }); });
  }, [projectId]);
  const tasks = useMemo(() => (data && data.tasks) || [], [data]);
  const allFolders = useMemo(() => tasks.flatMap((t) => t.folders.map((f) => ({ ...f, taskId: t.id }))), [tasks]);
  const allDocs = useMemo(() => tasks.flatMap((t) => t.documents.map((d) => ({ ...d, taskId: t.id }))), [tasks]);
  const { ticked, covered, toggle, items } = useTicks(allFolders, allDocs);
  // Files inside each folder (its subfolders too), per task.
  const filesIn = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => {
      const kids = (id) => t.folders.filter((f) => (f.parentId || null) === id);
      const count = (id) => t.documents.filter((d) => d.folderId === id).length + kids(id).reduce((n, f) => n + count(f.id), 0);
      t.folders.forEach((f) => m.set(`${t.id}:${f.id}`, count(f.id)));
    });
    return m;
  }, [tasks]);
  // How many files the ticks take (a ticked folder's, and the files ticked on their own).
  const fileCount = useMemo(() => {
    const going = new Set();
    tasks.forEach((t) => t.documents.forEach((d) => {
      if (ticked.has(`document:${t.id}:${d.id}`) || covered(d.folderId, t.id)) going.add(d.id);
    }));
    return going.size;
  }, [tasks, ticked, covered]);

  const flip = (key) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const rowsOf = (t, parentId, deep) => {
    const folders = t.folders.filter((f) => (f.parentId || null) === parentId && filesIn.get(`${t.id}:${f.id}`) > 0).sort((a, b) => (a.builtIn === b.builtIn ? 0 : a.builtIn ? -1 : 1));
    const docs = parentId ? t.documents.filter((d) => d.folderId === parentId).sort(byName) : [];
    return (
      <>
        {folders.map((f) => {
          const key = `${t.id}:${f.id}`;
          const inside = covered(f.parentId, t.id);
          const on = inside || ticked.has(`folder:${t.id}:${f.id}`);
          const isOpen = open.has(key);
          return (
            <React.Fragment key={f.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `4px 10px 4px ${10 + deep * 20}px`, fontSize: 13.5 }}>
                <input type="checkbox" style={check} checked={on} disabled={inside} onChange={() => toggle('folder', f.id, t.id)}
                  aria-label={`${f.name}, everything in it`} />
                <button type="button" onClick={() => flip(key)} aria-expanded={isOpen}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'var(--text-1)', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ display: 'inline-flex', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 120ms', color: 'var(--text-3)' }}><Glyph size={12}>{CHEVRON}</Glyph></span>
                  <Glyph>{FOLDER}</Glyph>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ color: 'var(--text-3)' }}>({filesIn.get(key)})</span>
                  {!f.builtIn && on && !inside && <span style={{ ...muted, fontSize: 12 }}>its subfolders too</span>}
                </button>
              </div>
              {isOpen && rowsOf(t, f.id, deep + 1)}
            </React.Fragment>
          );
        })}
        {docs.map((d) => {
          const inside = covered(d.folderId, t.id);
          return (
            <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `4px 10px 4px ${10 + deep * 20}px`, fontSize: 13.5, cursor: inside ? 'default' : 'pointer' }}>
              <input type="checkbox" style={check} checked={inside || ticked.has(`document:${t.id}:${d.id}`)} disabled={inside} onChange={() => toggle('document', d.id, t.id)} />
              <span style={{ width: 12 }} />
              <Glyph>{FILE}</Glyph>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.name}>{d.name}</span>
              {d.inCloseout && (
                <span title={`Sent ${new Date(d.inCloseout.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${d.inCloseout.by ? ` by ${d.inCloseout.by}` : ''}`}
                  style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', whiteSpace: 'nowrap' }}>In Closeout</span>
              )}
              <span className="mono" style={{ ...muted, fontSize: 12, whiteSpace: 'nowrap' }}>{formatBytes(d.size)}</span>
            </label>
          );
        })}
      </>
    );
  };

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', maxHeight: 460, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 8px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-1)' }}>The subs’ documents on this job</span>
        <span style={{ flex: 1 }} />
        <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={onClose}>Close</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 0' }}>
        {error && <div style={{ padding: '0 12px 6px' }}><ErrorBanner>{error}</ErrorBanner></div>}
        {!data && <div style={{ ...muted, padding: '6px 12px' }}>Reading the subs’ documents…</div>}
        {data && !data.on && <div style={{ ...muted, padding: '6px 12px' }}>Documents aren’t connected to E3 Subs yet.</div>}
        {data && data.on && !tasks.length && !error && <div style={{ ...muted, padding: '6px 12px' }}>No sub has a Documents task on this job.</div>}
        {tasks.map((t) => (
          <div key={t.id} style={{ paddingBottom: 6 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', padding: '6px 12px 2px', fontSize: 13 }}>
              <strong style={{ fontWeight: 650, color: 'var(--text-1)' }}>{t.sub}</strong>
              <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {t.name}</span>
              {t.code && <span className="mono" style={{ ...muted, fontSize: 11.5 }}>{t.code}</span>}
            </div>
            {t.documents.length ? rowsOf(t, null, 0) : <div style={{ ...muted, padding: '2px 12px 2px 33px' }}>Nothing uploaded yet.</div>}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', borderRadius: '0 0 10px 10px' }}>
        <AlreadyThere ask={sending.ask} busy={sending.busy} onAnswer={(c) => sending.answer(c).then((s) => { if (s) onClose(); })} />
        {!sending.ask && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <PrimaryButton busy={sending.busy} disabled={!fileCount} onClick={() => onAdd(items)} style={{ height: 32, fontSize: 13 }}>
              {fileCount ? `Add ${plural(fileCount, 'file')} here` : 'Add files here'}
            </PrimaryButton>
            <span style={muted}>into <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{folderName}</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}
